import { Box, Typography } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import SectionCard from "./SectionCard";
import { ANIMATE } from "../utils/motion";

/**
 * A donut chart with the total in the middle and a legend that lists EVERY category
 * with its count and share, so the chart can be read without relying on color.
 *
 * slices:    [{ key, label, count }]
 * colors:    { [key]: "#hex" }
 * onSliceClick(key): optional, makes slices and legend rows clickable
 */
export default function DonutCard({
  title,
  subtitle,
  slices,
  colors,
  centerLabel = "Total",
  onSliceClick,
  height = 250,
}) {
  const total = slices.reduce((sum, s) => sum + s.count, 0);
  const visible = slices.filter((s) => s.count > 0);

  return (
    <SectionCard title={title} subtitle={subtitle} sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ position: "relative", height }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={visible.length ? visible : [{ key: "none", label: "No data", count: 1 }]}
                dataKey="count"
                nameKey="label"
                innerRadius="64%"
                outerRadius="92%"
                paddingAngle={visible.length > 1 ? 3 : 0}
                cornerRadius={6}
                stroke="none"
                isAnimationActive={ANIMATE}
                startAngle={90}
                endAngle={-270}
                onClick={(entry) => onSliceClick && entry?.key && onSliceClick(entry.key)}
                style={{ cursor: onSliceClick ? "pointer" : "default", outline: "none" }}
              >
                {(visible.length ? visible : [{ key: "none" }]).map((s) => (
                  <Cell key={s.key} fill={colors[s.key] || "#E2E8F0"} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [`${value} (${Math.round((value * 100) / (total || 1))}%)`, name]}
                contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 24px rgba(15,23,42,.15)" }}
              />
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>
              {total}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {centerLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {slices.map((s) => (
            <Box
              key={s.key}
              onClick={onSliceClick ? () => onSliceClick(s.key) : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1,
                py: 0.75,
                borderRadius: 2,
                opacity: s.count === 0 ? 0.45 : 1,
                cursor: onSliceClick ? "pointer" : "default",
                "&:hover": onSliceClick ? { backgroundColor: "rgba(15,23,42,0.04)" } : undefined,
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: colors[s.key] || "#E2E8F0" }} />
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                {s.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {s.count}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 38, textAlign: "right" }}>
                {total ? Math.round((s.count * 100) / total) : 0}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </SectionCard>
  );
}
